import { PERSIST_INTERVAL_MS } from "./config";

// localStorage 永続化。書き込みはスロットルし、失敗 (QuotaExceeded等) は次回再試行。
// eval実測で14日分≈5KB と上限(≈5MB)に対し無視できるため、専用の縮退ロジックは持たない。
// サーバーミラー (remote.ts) と同じ文書形式を共有し、鮮度は savedAt で比較する。

const STORAGE_KEY = "sd3-state";

export interface PersistedState {
  sa?: number; // savedAt (epoch ms)。鮮度比較用。旧形式の文書には無い
  li: number | null; // lastInteractionAt (epoch ms)
  ss: number | null; // 進行中セッションの開始時刻 (epoch ms)
  days: Record<string, unknown>; // DayRecord 群 (load側で防御的にパース)
}

/** JSON.parse 済みの値を防御的に PersistedState へ。形式不明は null */
export const parsePersistedState = (parsed: unknown): PersistedState | null => {
  if (!parsed || typeof parsed !== "object") return null;
  const p = parsed as Partial<PersistedState>;
  return {
    ...(typeof p.sa === "number" ? { sa: p.sa } : {}),
    li: typeof p.li === "number" ? p.li : null,
    ss: typeof p.ss === "number" ? p.ss : null,
    days: p.days && typeof p.days === "object" ? p.days : {},
  };
};

/** 文書の鮮度 (epoch ms)。旧形式 (sa 無し) は最終操作時刻で代用する */
export const stateFreshness = (state: PersistedState): number => state.sa ?? state.li ?? 0;

export const loadState = (): PersistedState | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return parsePersistedState(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const saveState = (state: PersistedState): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // QuotaExceeded 等。次回スケジュールで再試行。
  }
};

export const clearState = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
};

/** スロットル付き保存スケジューラ。既にタイマーが立っていれば何もしない */
export class PersistScheduler {
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly getState: () => PersistedState,
    private readonly intervalMs: number = PERSIST_INTERVAL_MS,
    /** 保存直後フック (savedAt 刻印済みの文書を受け取る)。サーバーミラーの push 用 */
    private readonly onSaved?: (state: PersistedState) => void,
  ) {}

  schedule(): void {
    if (typeof window === "undefined" || this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.persistNow();
    }, this.intervalMs);
  }

  /** 即時保存 (reset時など) */
  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.persistNow();
  }

  private persistNow(): void {
    const stamped: PersistedState = { ...this.getState(), sa: Date.now() };
    saveState(stamped);
    try {
      this.onSaved?.(stamped);
    } catch {
      // ミラーの失敗で保存経路を止めない
    }
  }
}
